import React, { useState, Children, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Clock, Moon, AlertCircle, BookOpen, ArrowRight, Star, ShieldCheck, HelpCircle } from 'lucide-react';
// Animation variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function SalesPage() {
  const [modalOpen, setModalOpen] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen bg-[#0f1419] text-[#f5f0e8] font-sans selection:bg-[#e8913a] selection:text-white overflow-x-hidden" data-id="element-1">
      {/* Ambient Background Glow */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0" data-id="element-2">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-[#e8913a] opacity-[0.08] blur-[100px]" data-id="element-3" />
        <div className="absolute bottom-[10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-[#1d2530] opacity-[0.4] blur-[80px]" data-id="element-4" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-12 md:py-20" data-id="element-5">
        {/* HERO SECTION */}
        <motion.header initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="text-center mb-20" data-id="element-6">
          <motion.div variants={fadeIn} className="inline-flex items-center space-x-2 bg-[#1d2530] px-4 py-1.5 rounded-full text-sm font-medium text-[#e8913a] mb-6 border border-[#e8913a]/20" data-id="element-7">
            <Moon size={14} data-id="element-8" />
            <span data-id="element-9">Edición Noche (9pm–1am)</span>
          </motion.div>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl leading-[1.1] mb-6 text-white" data-id="element-10">
            Si en el día estás bien... pero de noche{' '}
            <span className="text-[#e8913a] italic" data-id="element-11">te pierdes</span>, esto es
            para ti.
          </motion.h1>

          <motion.p variants={fadeIn} className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-12">
            No es falta de disciplina: es que estás decidiendo en el peor minuto
            del día. Descubre el protocolo de 10 minutos para saber si es
            hambre... o emoción.
          </motion.p>

          <motion.div variants={fadeIn} className="relative mb-12 group" data-id="element-13">
            <div className="absolute inset-0 bg-[#e8913a] blur-[40px] opacity-20 group-hover:opacity-30 transition-opacity duration-700 rounded-full transform scale-75" data-id="element-14"></div>
            <img src="https://cdn.magicpatterns.com/uploads/x7ie665RKAwsKjiJxRetfe/HAMBRE_O_EMOCION_IMAGEN.png" alt="¿Hambre o Emoción? Libro Digital en tablet y laptop" className="relative rounded-xl shadow-2xl border border-white/10 mx-auto w-full max-w-2xl transform transition-transform duration-700 hover:scale-[1.01]" data-id="element-15" />
          </motion.div>

          <motion.div variants={fadeIn} data-id="element-16">
            <a href="#precio" onClick={e => {
            e.preventDefault();
            document.getElementById('precio')?.scrollIntoView({
              behavior: 'smooth'
            });
          }} className="inline-block bg-[#e8913a] hover:bg-[#f5a623] text-[#0f1419] font-bold text-lg px-10 py-4 rounded-full transition-all transform hover:scale-105 shadow-[0_0_30px_rgba(232,145,58,0.4)] w-full md:w-auto text-center" data-id="element-17">
              Quiero el protocolo
            </a>
            <p className="text-gray-500 text-sm mt-4" data-id="element-18">
              Descarga inmediata · PDF + Workbook + Tarjetas SOS
            </p>
          </motion.div>
        </motion.header>

        {/* OPENING LEAD */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="prose prose-lg prose-invert mx-auto mb-24" data-id="element-19">
          <p className="text-xl md:text-2xl font-serif text-white leading-relaxed" data-id="element-20">
            Hay una versión de ti que existe de 9pm a 1am. No es la misma
            persona que en la mañana toma buenas decisiones.
          </p>
          <p className="text-gray-300" data-id="element-21">
            En el día puedes. En el día eres "normal". Pero llega la noche, te
            sientas con el celular o Netflix, y sin darte cuenta estás caminando
            a la cocina como si alguien más manejara tu cuerpo.
          </p>
          <p className="text-gray-300" data-id="element-22">
            Y lo peor no es lo que comes. Lo peor es el minuto después. Cuando
            vuelves con el plato, te sientas, y una parte de ti ya sabe cómo
            termina:{' '}
            <strong className="text-[#e8913a]" data-id="element-23">
              alivio corto… culpa larga.
            </strong>
          </p>
          <p className="text-gray-300 border-l-4 border-[#e8913a] pl-6 italic" data-id="element-24">
            Esto no se arregla con motivación. Se arregla con un protocolo para
            el momento exacto en que te pierdes.
          </p>
        </motion.section>

        {/* IDENTIFICATION */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 bg-[#161b22] p-8 md:p-10 rounded-2xl border border-white/5" data-id="element-25">
          <motion.h2 variants={fadeIn} className="text-3xl md:text-4xl mb-8 text-white" data-id="element-26">
            Identificación brutal{' '}
            <span className="text-gray-500 text-xl block mt-2 font-sans font-normal" data-id="element-27">
              (sin insultar)
            </span>
          </motion.h2>

          <motion.div variants={fadeIn} className="space-y-6 text-gray-300" data-id="element-28">
            <p data-id="element-29">
              Si tu problema fuera "no sé qué comer", ya lo habrías resuelto. Tú
              sabes qué es "saludable" y qué deberías hacer.
            </p>
            <p data-id="element-30">
              Pero tu patrón no ocurre en un lugar lógico. Ocurre en el lugar
              donde tu cerebro está cansado, tu cuerpo está en modo "apágate", y
              tu vida por fin se queda en silencio.{' '}
              <strong className="text-white" data-id="element-31">La noche.</strong>
            </p>
            <p data-id="element-32">Ahí no estás negociando con comida. Estás negociando con:</p>
            <ul className="space-y-3 mt-4" data-id="element-33">
              {['Cansancio acumulado', 'Emoción no procesada', "Un hábito que tu cuerpo aprendió como 'calma rápida'"].map((item, i) => <li key={i} className="flex items-start" data-id="element-34">
                  <span className="bg-[#e8913a]/20 text-[#e8913a] p-1 rounded-full mr-3 mt-1" data-id="element-35">
                    <X size={14} data-id="element-36" />
                  </span>
                  <span data-id="element-37">{item}</span>
                </li>)}
            </ul>
            <p className="mt-6 pt-6 border-t border-white/10" data-id="element-38">
              Y por eso "fuerza de voluntad" es una trampa. Porque a esa hora,
              ya la gastaste.
            </p>
          </motion.div>
        </motion.section>

        {/* THE CYCLE */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="mb-24 text-center" data-id="element-39">
          <h2 className="text-3xl md:text-4xl mb-10 text-white" data-id="element-40">
            El ciclo que te está entrenando
          </h2>

          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6 text-sm md:text-base font-medium text-gray-300" data-id="element-41">
            {['Pantalla', 'Impulso', 'Cocina', 'Alivio', 'Culpa', 'Mañana empiezo'].map((step, i, arr) => <Fragment key={i} data-id="element-42">
                <div className="bg-[#1d2530] px-4 py-3 rounded-lg border border-white/5 shadow-lg" data-id="element-43">
                  {step}
                </div>
                {i < arr.length - 1 && <ArrowRight className="text-[#e8913a] opacity-50" size={20} data-id="element-44" />}
              </Fragment>)}
          </div>

          <p className="mt-10 text-gray-400 max-w-2xl mx-auto" data-id="element-45">
            Cada vez que pasa, el circuito se fortalece. No porque seas débil.
            Sino porque el cerebro ama dos cosas:{' '}
            <span className="text-white" data-id="element-46">lo familiar</span> y{' '}
            <span className="text-white" data-id="element-47">lo que alivia rápido</span>.
          </p>
        </motion.section>

        {/* WHY OLD SOLUTIONS FAIL */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24" data-id="element-48">
          <motion.h2 variants={fadeIn} className="text-3xl md:text-4xl mb-10 text-center text-white" data-id="element-49">
            Por qué lo que has intentado falla
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-50">
            {[{
            title: 'Dietas / Prohibición',
            desc: 'Te dejan con sensación de escasez. La noche te cobra.'
          }, {
            title: '“No compres snacks”',
            desc: 'La emoción no se va; solo cambia el objeto.'
          }, {
            title: 'Tips sueltos',
            desc: 'Sirven cuando estás tranquilo/a, no cuando estás en piloto automático.'
          }, {
            title: 'Aguantar',
            desc: 'Convierte un impulso en guerra… y las guerras se pierden de madrugada.'
          }].map((item, i) => <motion.div key={i} variants={fadeIn} className="bg-[#161b22] p-6 rounded-xl border border-white/5 hover:border-[#e8913a]/30 transition-colors" data-id="element-51">
                <h3 className="text-xl font-serif text-[#e8913a] mb-2" data-id="element-52">
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-53">{item.desc}</p>
              </motion.div>)}
          </div>

          <motion.div variants={fadeIn} className="mt-10 text-center" data-id="element-54">
            <p className="text-lg text-white" data-id="element-55">
              Tu problema no es información. Tu problema es{' '}
              <span className="text-[#e8913a] font-bold" data-id="element-56">timing</span>.
            </p>
            <p className="text-gray-400" data-id="element-57">
              Estás intentando decidir bien en el peor minuto del día.
            </p>
          </motion.div>
        </motion.section>

        {/* THE SOLUTION */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#e8913a]/10 to-[#0f1419] border border-[#e8913a]/20 p-8 md:p-12" data-id="element-58">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#e8913a] opacity-5 blur-[80px] rounded-full pointer-events-none" data-id="element-59"></div>

          <motion.div variants={fadeIn} className="text-center mb-12" data-id="element-60">
            <span className="text-[#e8913a] font-bold tracking-wider text-sm uppercase mb-2 block" data-id="element-61">
              La Solución Real
            </span>
            <h2 className="text-3xl md:text-5xl text-white mb-6" data-id="element-62">
              Un protocolo de 10 minutos
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto" data-id="element-63">
              No es una dieta. No es un plan de comidas. Es un sistema para usar{' '}
              <strong className="text-white" data-id="element-64">cuando te da el impulso</strong>.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-65">
            <motion.div variants={fadeIn} className="bg-[#0f1419]/80 p-6 rounded-2xl border border-white/10" data-id="element-66">
              <div className="w-12 h-12 bg-[#e8913a]/20 rounded-full flex items-center justify-center text-[#e8913a] mb-4" data-id="element-67">
                <AlertCircle size={24} data-id="element-68" />
              </div>
              <h3 className="text-2xl font-serif text-white mb-3" data-id="element-69">
                1. Semáforo de Saciedad
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-70">
                En 30 segundos ubicas tu cuerpo: ¿realmente estás en 3–4
                (hambre) o estás en 5–6 (emoción/ritual)? Una herramienta visual
                para salir de la duda.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-[#0f1419]/80 p-6 rounded-2xl border border-white/10" data-id="element-71">
              <div className="w-12 h-12 bg-[#e8913a]/20 rounded-full flex items-center justify-center text-[#e8913a] mb-4" data-id="element-72">
                <Clock size={24} data-id="element-73" />
              </div>
              <h3 className="text-2xl font-serif text-white mb-3" data-id="element-74">
                2. Puente de 10 Min
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-75">
                Creas un espacio entre impulso y acción. No para "prohibirte",
                sino para volver a elegir sin trance. Aplicas 1 de 3 resets
                (cuerpo, mente, entorno) y luego decides.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* BENEFITS */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24" data-id="element-76">
          <motion.h2 variants={fadeIn} className="text-3xl md:text-4xl mb-10 text-center text-white" data-id="element-77">
            Qué cambia cuando tienes un guion
          </motion.h2>

          <div className="space-y-4 max-w-2xl mx-auto" data-id="element-78">
            {['Dejas de preguntarte "¿qué me pasa?"', 'Dejas de prometer "mañana empiezo"', 'Empiezas a detectar el patrón antes', 'Si comes, comes consciente y paras a tiempo', 'Si no comes, no sientes castigo: sientes control'].map((benefit, i) => <motion.div key={i} variants={fadeIn} className="flex items-center p-4 bg-[#161b22] rounded-lg border border-white/5" data-id="element-79">
                <Check className="text-[#e8913a] mr-4 flex-shrink-0" size={20} data-id="element-80" />
                <span className="text-gray-200" data-id="element-81">{benefit}</span>
              </motion.div>)}
          </div>
          <motion.p variants={fadeIn} className="text-center mt-8 text-gray-400 italic" data-id="element-82">
            No te conviertes en una persona perfecta. Te conviertes en una
            persona con un sistema.
          </motion.p>
        </motion.section>

        {/* DELIVERABLES */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 bg-[#1d2530] rounded-3xl p-8 md:p-12 border border-white/5" data-id="element-83">
          <div className="flex flex-col md:flex-row items-center gap-10" data-id="element-84">
            <motion.div variants={fadeIn} className="flex-1" data-id="element-85">
              <h2 className="text-3xl font-serif text-white mb-6" data-id="element-86">
                Qué incluye
              </h2>
              <ul className="space-y-6" data-id="element-87">
                <li className="flex items-start" data-id="element-88">
                  <BookOpen className="text-[#e8913a] mr-4 mt-1 flex-shrink-0" size={24} data-id="element-89" />
                  <div data-id="element-90">
                    <strong className="block text-white text-lg" data-id="element-91">
                      El PDF completo Edición Noche
                    </strong>
                    <span className="text-gray-400 text-sm" data-id="element-92">
                      Tu guía paso a paso para las noches de 9pm a 1am.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-93">
                  <Star className="text-[#e8913a] mr-4 mt-1 flex-shrink-0" size={24} data-id="element-94" />
                  <div data-id="element-95">
                    <strong className="block text-white text-lg" data-id="element-96">
                      Semáforo + Tracker de 14 Noches
                    </strong>
                    <span className="text-gray-400 text-sm" data-id="element-97">
                      Ubica tu nivel de saciedad y registra tu progreso noche a
                      noche.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-98">
                  <ShieldCheck className="text-[#e8913a] mr-4 mt-1 flex-shrink-0" size={24} data-id="element-99" />
                  <div data-id="element-100">
                    <strong className="block text-white text-lg" data-id="element-101">
                      Tarjetas SOS 11pm
                    </strong>
                    <span className="text-gray-400 text-sm" data-id="element-102">
                      Preguntas rápidas para salir del piloto automático en el
                      momento exacto.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-103">
                  <Clock className="text-[#e8913a] mr-4 mt-1 flex-shrink-0" size={24} data-id="element-104" />
                  <div data-id="element-105">
                    <strong className="block text-white text-lg" data-id="element-106">
                      Rutina de Cierre
                    </strong>
                    <span className="text-gray-400 text-sm" data-id="element-107">
                      Tu protocolo de 10 minutos para cerrar el día sin caos.
                    </span>
                  </div>
                </li>
              </ul>
            </motion.div>
            <motion.div variants={fadeIn} className="w-full md:w-1/3" data-id="element-108">
              <div className="aspect-[3/4] bg-[#0f1419] rounded-xl border border-white/10 flex items-center justify-center relative overflow-hidden" data-id="element-109">
                <div className="absolute inset-0 bg-gradient-to-t from-[#e8913a]/20 to-transparent opacity-50" data-id="element-110"></div>
                <img src="https://cdn.magicpatterns.com/uploads/x7ie665RKAwsKjiJxRetfe/HAMBRE_O_EMOCION_IMAGEN.png" alt="Mockup del producto" className="w-full h-full object-cover opacity-90" data-id="element-111" />
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* AUDIENCE */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 grid md:grid-cols-2 gap-8" data-id="element-112">
          <motion.div variants={fadeIn} className="bg-[#161b22] p-8 rounded-2xl border-l-4 border-[#e8913a]" data-id="element-113">
            <h3 className="text-xl font-serif text-white mb-4" data-id="element-114">
              Esto es para ti si:
            </h3>
            <ul className="space-y-3 text-gray-300" data-id="element-115">
              <li className="flex items-start" data-id="element-116">
                <Check size={16} className="text-[#e8913a] mr-2 mt-1" data-id="element-117" /> En el
                día estás "bien", pero en la noche te pierdes
              </li>
              <li className="flex items-start" data-id="element-118">
                <Check size={16} className="text-[#e8913a] mr-2 mt-1" data-id="element-119" /> Comes
                con pantalla y luego sientes culpa
              </li>
              <li className="flex items-start" data-id="element-120">
                <Check size={16} className="text-[#e8913a] mr-2 mt-1" data-id="element-121" /> Quieres
                parar el piloto automático sin dietas
              </li>
            </ul>
          </motion.div>

          <motion.div variants={fadeIn} className="bg-[#161b22] p-8 rounded-2xl border-l-4 border-gray-600 opacity-70" data-id="element-122">
            <h3 className="text-xl font-serif text-white mb-4" data-id="element-123">
              No es para ti si:
            </h3>
            <ul className="space-y-3 text-gray-400" data-id="element-124">
              <li className="flex items-start" data-id="element-125">
                <X size={16} className="text-gray-500 mr-2 mt-1" data-id="element-126" /> Buscas
                promesas de pérdida de peso rápida
              </li>
              <li className="flex items-start" data-id="element-127">
                <X size={16} className="text-gray-500 mr-2 mt-1" data-id="element-128" /> Quieres
                reglas extremas o prohibiciones
              </li>
              <li className="flex items-start" data-id="element-129">
                <X size={16} className="text-gray-500 mr-2 mt-1" data-id="element-130" /> Estás en un
                TCA severo sin apoyo profesional
              </li>
            </ul>
          </motion.div>
        </motion.section>

        {/* FAQ */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24" data-id="element-131">
          <motion.h2 variants={fadeIn} className="text-3xl md:text-4xl mb-10 text-center text-white" data-id="element-132">
            Preguntas Frecuentes
          </motion.h2>

          <div className="space-y-6" data-id="element-133">
            {[{
            q: '¿Y si de verdad tengo hambre?',
            a: 'Entonces comes. El objetivo no es aguantar hambre. Es dejar de comer en trance.'
          }, {
            q: '¿Y si no se me quita el antojo?',
            a: 'A veces no se quita rápido. Pero tú sí sales del pico. Y desde ahí decides diferente.'
          }, {
            q: '¿Tengo que dejar la pantalla?',
            a: "No. Pero sí vas a aprender a romper el ritual cuando la pantalla se convierte en 'hipnosis + snack'."
          }].map((faq, i) => <motion.div key={i} variants={fadeIn} className="bg-[#161b22] p-6 rounded-xl border border-white/5" data-id="element-134">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center" data-id="element-135">
                  <HelpCircle size={18} className="text-[#e8913a] mr-3" data-id="element-136" />
                  {faq.q}
                </h3>
                <p className="text-gray-400 ml-8" data-id="element-137">{faq.a}</p>
              </motion.div>)}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section id="precio" initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="text-center bg-gradient-to-b from-[#1d2530] to-[#0f1419] p-10 md:p-16 rounded-3xl border border-[#e8913a]/30 shadow-2xl relative overflow-hidden" data-id="element-138">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-139"></div>
          <div className="relative z-10" data-id="element-140">
            <h2 className="text-3xl md:text-5xl font-serif text-white mb-4" data-id="element-141">
              ¿HAMBRE O EMOCIÓN?
            </h2>
            <p className="text-xl text-[#e8913a] mb-8 font-medium" data-id="element-142">
              Edición Noche (9pm–1am)
            </p>

            <div className="flex items-center justify-center gap-4 mb-2" data-id="element-143">
              <span className="text-2xl text-gray-500 line-through" data-id="element-144">$29</span>
              <span className="text-5xl font-bold text-white" data-id="element-145">$6.36</span>
            </div>
            <div className="inline-flex items-center bg-green-500/20 text-green-400 text-sm font-semibold px-3 py-1 rounded-full mb-2" data-id="element-146">
              78% OFF — Precio de lanzamiento
            </div>
            <p className="text-gray-400 text-sm mb-10" data-id="element-147">
              Pago único. Acceso inmediato.
            </p>

            <a href="https://coresolutionsg.com/cart/47458839068859:1" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#e8913a] hover:bg-[#f5a623] text-[#0f1419] font-bold text-lg px-10 py-4 rounded-full transition-all transform hover:scale-105 shadow-[0_0_30px_rgba(232,145,58,0.4)] mb-8 w-full md:w-auto text-center" data-id="element-148">
              Comprar Ahora y Usar Esta Noche
            </a>

            <p className="text-gray-400 max-w-lg mx-auto text-sm leading-relaxed" data-id="element-149">
              Si tu noche se volvió un lugar donde te traicionas… no necesitas
              otra promesa. Necesitas un plan para el minuto exacto. Diez
              minutos. Un puente. Una decisión limpia.
            </p>
          </div>
        </motion.section>

        <footer className="mt-20 text-center text-gray-600 text-sm pb-10 space-y-3" data-id="element-150">
          <p data-id="element-151">© 2024 Hambre o Emoción. Todos los derechos reservados.</p>
          <div className="flex items-center justify-center gap-4" data-id="element-152">
            <button onClick={() => setModalOpen('privacy')} className="underline underline-offset-2 hover:text-gray-400 transition-colors" data-id="element-153">
              Privacy Policy
            </button>
            <span className="text-gray-700" data-id="element-154">·</span>
            <button onClick={() => setModalOpen('terms')} className="underline underline-offset-2 hover:text-gray-400 transition-colors" data-id="element-155">
              Terms &amp; Conditions
            </button>
          </div>
        </footer>
      </div>

      {/* PRIVACY / TERMS MODAL */}
      <AnimatePresence data-id="element-156">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setModalOpen(null)} data-id="element-157">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-[#161b22] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-158">
              <div className="sticky top-0 bg-[#161b22] border-b border-white/10 px-6 py-4 flex items-center justify-between rounded-t-2xl" data-id="element-159">
                <h2 className="text-xl font-serif text-white" data-id="element-160">
                  {modalOpen === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions'}
                </h2>
                <button onClick={() => setModalOpen(null)} className="text-gray-400 hover:text-white transition-colors p-1" data-id="element-161">
                  <X size={20} data-id="element-162" />
                </button>
              </div>

              <div className="px-6 py-6 text-gray-300 text-sm leading-relaxed space-y-4" data-id="element-163">
                {modalOpen === 'privacy' ? <>
                    <p data-id="element-164">
                      <strong className="text-white" data-id="element-165">
                        Última actualización:
                      </strong>{' '}
                      Enero 2025
                    </p>
                    <p data-id="element-166">
                      En Hambre o Emoción ("nosotros", "nuestro") respetamos tu
                      privacidad y nos comprometemos a proteger tu información
                      personal.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-167">
                      1. Información que recopilamos
                    </h3>
                    <p data-id="element-168">
                      Recopilamos únicamente la información necesaria para
                      procesar tu compra: nombre, dirección de correo
                      electrónico y datos de pago. No almacenamos datos de
                      tarjetas de crédito directamente; estos son procesados de
                      forma segura por nuestro proveedor de pagos.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-169">
                      2. Uso de la información
                    </h3>
                    <p data-id="element-170">
                      Tu información se utiliza exclusivamente para: procesar y
                      entregar tu compra digital, enviarte el producto adquirido
                      por correo electrónico, y comunicarnos contigo sobre tu
                      pedido si es necesario.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-171">
                      3. Compartir información
                    </h3>
                    <p data-id="element-172">
                      No vendemos, intercambiamos ni transferimos tu información
                      personal a terceros. La única excepción son los
                      proveedores de servicios que nos ayudan a operar
                      (procesador de pagos, servicio de email), quienes están
                      obligados a mantener la confidencialidad.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-173">
                      4. Seguridad
                    </h3>
                    <p data-id="element-174">
                      Implementamos medidas de seguridad razonables para
                      proteger tu información personal contra acceso no
                      autorizado, alteración o destrucción.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-175">
                      5. Cookies
                    </h3>
                    <p data-id="element-176">
                      Nuestro sitio puede utilizar cookies para mejorar la
                      experiencia del usuario. Puedes configurar tu navegador
                      para rechazar cookies, aunque esto podría afectar algunas
                      funcionalidades.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-177">
                      6. Tus derechos
                    </h3>
                    <p data-id="element-178">
                      Tienes derecho a acceder, corregir o eliminar tu
                      información personal en cualquier momento. Para ejercer
                      estos derechos, contáctanos a través del correo
                      proporcionado en tu confirmación de compra.
                    </p>
                  </> : <>
                    <p data-id="element-179">
                      <strong className="text-white" data-id="element-180">
                        Última actualización:
                      </strong>{' '}
                      Enero 2025
                    </p>
                    <p data-id="element-181">
                      Al comprar y utilizar "¿Hambre o Emoción? — Edición
                      Noche", aceptas los siguientes términos y condiciones.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-182">
                      1. Producto digital
                    </h3>
                    <p data-id="element-183">
                      Este es un producto digital (PDF, workbook, tarjetas y
                      plantilla) que se entrega electrónicamente tras la compra.
                      Debido a la naturaleza digital del producto, todas las
                      ventas son finales.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-184">
                      2. Uso personal
                    </h3>
                    <p data-id="element-185">
                      El contenido adquirido es para uso personal únicamente. No
                      está permitido redistribuir, revender, copiar o compartir
                      el material con terceros sin autorización expresa por
                      escrito.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-186">
                      3. Propiedad intelectual
                    </h3>
                    <p data-id="element-187">
                      Todo el contenido, incluyendo textos, diseños, gráficos y
                      metodologías, es propiedad intelectual de Hambre o Emoción
                      y está protegido por las leyes de derechos de autor.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-188">
                      4. Descargo de responsabilidad
                    </h3>
                    <p data-id="element-189">
                      Este producto es de carácter educativo e informativo. No
                      sustituye el consejo médico, nutricional o psicológico
                      profesional. Si padeces un trastorno de conducta
                      alimentaria (TCA) severo, te recomendamos buscar apoyo
                      profesional.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-190">
                      5. Resultados
                    </h3>
                    <p data-id="element-191">
                      Los resultados pueden variar de persona a persona. No
                      garantizamos resultados específicos, ya que dependen del
                      uso individual del material y las circunstancias
                      personales de cada usuario.
                    </p>
                    <h3 className="text-white font-bold text-base mt-6" data-id="element-192">
                      6. Modificaciones
                    </h3>
                    <p data-id="element-193">
                      Nos reservamos el derecho de modificar estos términos en
                      cualquier momento. Los cambios serán efectivos desde su
                      publicación en esta página.
                    </p>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}